import { css } from '@emotion/react';
import {
  faGlobe, faRefresh,
} from '@fortawesome/pro-solid-svg-icons';
import {
  type FC, useCallback, useEffect, useRef, useState,
} from 'react';
import { AlertComponent } from '~/_shared/baseComponents/alert';
import { AutocompleteComponent } from '~/_shared/baseComponents/autocomplete';
import {
  IconButtonComponent, IconButtonStyle,
} from '~/_shared/baseComponents/buttons';
import {
  DropDownItemSize, getRegularDropdownItemSize,
} from '~/_shared/baseComponents/dropdown';
import {
  InputSize, InputWithSpinnersComponent,
} from '~/_shared/baseComponents/inputs';
import { LoaderComponent } from '~/_shared/baseComponents/loaders';
import {
  RadioGroupComponent, type RadioGroupItem,
} from '~/_shared/baseComponents/radio/radioGroup.component';
import {
  TooltipBehavior, TooltipComponent, TooltipPlacement,
} from '~/_shared/baseComponents/tooltip/tooltip.component';
import { MapColumnPickerDropdownContainer } from '~/_shared/components/mapColumnPickerDropdown/mapColumnPickerDropdown.container';
import { MapPickerDropdownContainer } from '~/_shared/components/mapPickerDropdown/mapPickerDropdown.container';
import { CREATE_NEW_SALES_REP_MAP_ACTION } from '~/_shared/hooks/appLoad/useCreateSalesRepMapOnAppLoad.hook';
import { useTheme } from '~/_shared/themes/theme.hooks';
import { type ThemeProps } from '~/_shared/types/themeProps';
import { createUuid } from '~/_shared/utils/createUuid';
import { delay } from '~/_shared/utils/delay';
import {
  type TranslationFnc, useTranslation,
} from '~/_shared/utils/hooks';
import { usePrevious } from '~/_shared/utils/hooks/usePrevious';
import {
  isNullOrUndefined, notNullOrUndefined, notNullsy,
} from '~/_shared/utils/typeGuards';
import { type BoundaryGroup } from '~/boundary/boundary.types';
import { ContinueButton } from '~/map/layered/createLayeredMapModal/ContinueButton';
import { type MapListingItem } from '~/map/listing/item/mapListingItem';
import {
  type MetricValue, NumberOfTerritoriesLastUpdateReason, type SalesRepLocationsMap,
} from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createBoundaryGroupFromAIModal/createBoundaryGroupFromAIModal.types';
import { DataWeightsItemComponent } from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createBoundaryGroupFromAIModal/modalSteps/dataWeightsItem.component';
import {
  OPTIMIZED_TERRITORY_AP_IMPORTANCE_MAX, OPTIMIZED_TERRITORY_AP_IMPORTANCE_MIN_UI,
} from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createOptimizedTerritoriesModal/createOptimizedTerritoriesModal.container';
import { EXTERNAL_ACTION_INDICATOR } from '~/spreadsheet/googleSpreadsheet/googleSheet.repository';
import { AdditionalOptionsConstraintsComponent } from './additionalOptionsConstraints.component';
import type { WMSConstraintValidationState } from './constraintsValidation/useWMSConstraintsValidation';

export const WMS_MIN_NUMBER_OF_TERRITORIES = 2;

const getNumberOfTerritoriesUpdatedReasonMessage = (reason: NumberOfTerritoriesLastUpdateReason, t: TranslationFnc) => {
  if (reason === NumberOfTerritoriesLastUpdateReason.RespectPreviousTerritoriesChange) {
    return t('boundaryAI.We changed your requested number of territories to match the number of the previous territories.');
  }

  if (reason === NumberOfTerritoriesLastUpdateReason.SalesRepLocationsMapIdChange) {
    return t('boundaryAI.We changed your requested number of territories to match the number of sales reps.');
  }

  return null;
};

const contentContainerStyle = css({
  marginTop: 10,
  paddingLeft: 25,
});

const radioLabelStyle = css({
  textTransform: 'uppercase',
  fontSize: 14,
  lineHeight: 1,
  whiteSpace: 'nowrap',
});

const anchorPointsImportanceWrapperStyle = css({
  padding: 16,
});

const selectLabelStyle = css({
  marginBottom: 5,
});

const selectMapContainer = css({
  display: 'flex',
  width: '100%',
  alignItems: 'center',
  gap: 10,
});

const continueButtonStyle = css({
  display: 'flex',
  justifyContent: 'flex-end',
  marginTop: 20,
});

const validationAlertStyle = css({
  marginTop: 16,
});

const dropdownItemStyle = ({ theme, size }: ThemeProps<{ size: InputSize}>) => css({
  fontSize: 16,
  lineHeight: '20px',
  padding: '0 8px',
  height: getRegularDropdownItemSize(size, DropDownItemSize.Default),
  textAlign: 'left',
  color: theme.textColors.secondary,
  display: 'block',
  whiteSpace: 'nowrap',
  background: 'none',
  border: `1px solid ${theme.borderColors.primary}`,
  borderWidth: '1px 0 0',
  width: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  backgroundColor: theme.backgroundColors.secondary,
  cursor: 'pointer',
});

const radioItemStyle = css({
  display: 'flex',
  flexDirection: 'column',
});

const numberOfTerritoriesChildrenStyle = css({
  display: 'flex',
  alignItems: 'center',
});

const refreshTerritoriesIconStyle = css({
  marginLeft: 10,
});

export enum CreateOptimizedTerritoriesAdditionalOption {
  SalesReps = 'SalesReps',
  RespectPreviousTerritories = 'RespectPreviousTerritories',
  NumerOfTerritories = 'NumerOfTerritories',
  Constraints = 'Constraints',
}

export type CreateOptimizedTerritoryAdditionalStepInterlock = {
  lockingItem: CreateOptimizedTerritoriesAdditionalOption;
  item: CreateOptimizedTerritoriesAdditionalOption;
  reason: string;
};

export type AdditionalOptionsStepComponentProps = Readonly<{
  interlocks: readonly CreateOptimizedTerritoryAdditionalStepInterlock[];
  customBoundaryGroups: readonly BoundaryGroup[];
  dataMetricsValues: readonly MetricValue[];
  demographicsValues: readonly MetricValue[];
  locationCountValidationError: string | null;
  numberOfTerritories: number | null;
  numberOfTerritoriesLoading: boolean;
  numberOfTerritoriesUpdateReason: NumberOfTerritoriesLastUpdateReason;
  onChangeDataMetrics: (dataMetricsValues: readonly MetricValue[]) => void;
  onChangeDemographics: (demographicsValues: readonly MetricValue[]) => void;
  onChangeNumberOfTerritories: (value: number) => void;
  onChangeOverrideTerritory: (flag: boolean) => void;
  onChangePreviouslySavedBoundaryGroupId: (previouslySavedBoundaryGroupId: number) => void;
  onChangeSelectedOption: (option: CreateOptimizedTerritoriesAdditionalOption) => void;
  onChangeSalesRepLocationsMap: (map: SalesRepLocationsMap) => void;
  onChangeSalesRepNameColumnId: (columnId: string) => void;
  onContinue: () => void;
  onRefreshTerritoriesCount: () => void;
  overrideTerritory: boolean;
  previouslySavedBoundaryGroupId: number | null;
  salesRepLocationsMap: SalesRepLocationsMap | null;
  salesRepNameColumnId: string | null;
  selectedOption: CreateOptimizedTerritoriesAdditionalOption | null;
  showRespectPreviousTerritories: boolean;
  onAnchorPointsImportanceChange?: (value: number) => void;
  anchorPointsImportance?: number;
  constraintValidationState: WMSConstraintValidationState;
}>;

export const AdditionalOptionsStepComponent: FC<AdditionalOptionsStepComponentProps> = ({
  customBoundaryGroups,
  dataMetricsValues,
  demographicsValues,
  locationCountValidationError,
  numberOfTerritories,
  numberOfTerritoriesUpdateReason,
  onChangeDataMetrics,
  onChangeDemographics,
  onChangeNumberOfTerritories,
  onChangePreviouslySavedBoundaryGroupId,
  onChangeSelectedOption,
  onChangeSalesRepLocationsMap,
  onChangeSalesRepNameColumnId,
  onContinue,
  numberOfTerritoriesLoading,
  previouslySavedBoundaryGroupId,
  salesRepLocationsMap,
  salesRepNameColumnId,
  selectedOption,
  onAnchorPointsImportanceChange,
  anchorPointsImportance,
  constraintValidationState: {
    isLoading: constraintsValidationLoading,
    validationData: constraintsValidationData,
    areConstraintsValid,
    generalError: constraintsGeneralError,
  },
  showRespectPreviousTerritories,
}: AdditionalOptionsStepComponentProps) => {
  const [salesRepMap, setSalesRepMap] = useState<MapListingItem | null>(null);
  const [lastChangeNumberOfTerritories, setLastChangeNumberOfTerritories] = useState({ reason: NumberOfTerritoriesLastUpdateReason.Other, id: createUuid() });
  const previousNumberOfTerritories = usePrevious(numberOfTerritories);
  const forceRefreshCallbackRef = useRef<() => void>(() => null);
  const [t] = useTranslation();
  const theme = useTheme();

  const numberOfTerritoriesChangedMessage = getNumberOfTerritoriesUpdatedReasonMessage(lastChangeNumberOfTerritories.reason, t);

  const onOpenCreateSalesRepMapTab = useCallback(() => {
    window.open(
      `${location.protocol}//${location.host}?${EXTERNAL_ACTION_INDICATOR}=${CREATE_NEW_SALES_REP_MAP_ACTION}`,
      '_blank'
    )?.focus();
  }, []);

  const onRefreshMaps = useCallback(() => {
    forceRefreshCallbackRef.current();
  }, []);

  const onSalesRepMapChange = useCallback((map: MapListingItem) => {
    setSalesRepMap(map);
    const spreadsheetId = map.spreadSheets[0]?.spreadsheetId;
    if (!spreadsheetId) {
      return;
    }

    onChangeSalesRepLocationsMap({ mapId: map.id, spreadsheetId });
  }, [onChangeSalesRepLocationsMap]);

  useEffect(() => {
    if (numberOfTerritoriesUpdateReason === NumberOfTerritoriesLastUpdateReason.Other
      || numberOfTerritoriesUpdateReason === NumberOfTerritoriesLastUpdateReason.ManualEdit
      || previousNumberOfTerritories !== undefined && notNullOrUndefined(numberOfTerritories) && previousNumberOfTerritories !== numberOfTerritories) {
      const newReasonId = createUuid();

      // use delay to update the message in next render cycle
      // tooltip needs the component already rendered / updated to calculate position properly
      delay(1).then(() => setLastChangeNumberOfTerritories({ reason: numberOfTerritoriesUpdateReason, id: newReasonId }));

      const clearUpdatedReason = () => {
        setLastChangeNumberOfTerritories(previous => previous.id === newReasonId
          ? { reason: NumberOfTerritoriesLastUpdateReason.Other, id: createUuid() }
          : previous
        );
      };

      // use delay so the reason isn't cleared within the same onclick event that is bubling up
      delay(1).then(() => window.addEventListener('click', clearUpdatedReason, { once: true }));
    }
  }, [numberOfTerritories, numberOfTerritoriesUpdateReason, previousNumberOfTerritories]);

  const respectSalesRepOptionsValid = selectedOption !== CreateOptimizedTerritoriesAdditionalOption.SalesReps || (salesRepLocationsMap && salesRepNameColumnId);
  const respectPreviousTerritoriesValid = selectedOption !== CreateOptimizedTerritoriesAdditionalOption.RespectPreviousTerritories || previouslySavedBoundaryGroupId;
  const numberOfTerritoriesValid = (selectedOption !== CreateOptimizedTerritoriesAdditionalOption.NumerOfTerritories || notNullOrUndefined(numberOfTerritories)) && !numberOfTerritoriesLoading;
  const constraintsActive = dataMetricsValues.find(i => i.isConstraint) || demographicsValues.find(i => i.isConstraint);
  const constraintsOptionsValid = selectedOption !== CreateOptimizedTerritoriesAdditionalOption.Constraints || (constraintsActive && !constraintsValidationLoading && areConstraintsValid);
  const isSubmitDisabled = !selectedOption || !respectSalesRepOptionsValid || !respectPreviousTerritoriesValid || !numberOfTerritoriesValid
    || !constraintsOptionsValid || !!locationCountValidationError;

  const radioItems: RadioGroupItem<CreateOptimizedTerritoriesAdditionalOption>[] = [
    {
      value: CreateOptimizedTerritoriesAdditionalOption.SalesReps,
      label: <span css={radioLabelStyle}>{t('boundaryAI.Respect Sales Reps Locations')}</span>,
      children: selectedOption === CreateOptimizedTerritoriesAdditionalOption.SalesReps ? (
        <div css={contentContainerStyle}>
          <p css={selectLabelStyle}>{t('boundaryAI.Select Map Containing Sales Rep Locations')}</p>
          <div css={selectMapContainer}>
            <MapPickerDropdownContainer
              onMapSelected={onSalesRepMapChange}
              postFetchFilter={() => true}
              selectedMap={null}
              placeholder={t('boundaryAI.Select Map with Sales People...')}
              inputSize={InputSize.Medium}
              additionalAction={(
                <button
                  css={dropdownItemStyle({ theme, size: InputSize.Medium })}
                  onClick={onOpenCreateSalesRepMapTab}
                >
                  {t('boundaryAI.+ Add New Map with sales people')}
                </button>
              )}
              forceRefreshCallbackRef={forceRefreshCallbackRef}
            />
            <IconButtonComponent
              icon={faRefresh}
              onClick={onRefreshMaps}
              buttonStyle={IconButtonStyle.Secondary}
            />
          </div>
          {salesRepMap && salesRepMap.spreadSheets[0] && (
            <>
              <p css={selectLabelStyle}>{t('boundaryAI.Select Data Column with Sales Rep...')}</p>
              <MapColumnPickerDropdownContainer
                mapId={salesRepMap.id}
                virtualSpreadsheetId={salesRepMap.spreadSheets[0].spreadsheetId}
                onChange={onChangeSalesRepNameColumnId}
                triggerHeight={InputSize.Medium}
                placeholder={t('Select Name Column')}
              />
            </>
          )}
          {(onAnchorPointsImportanceChange && (anchorPointsImportance !== undefined)) && (
            <div css={anchorPointsImportanceWrapperStyle}>
              <DataWeightsItemComponent
                onChange={(metric) => onAnchorPointsImportanceChange(metric.weight)}
                metric={{
                  id: 'ap_importance',
                  weight: anchorPointsImportance,
                  name: t('Sales Rep Location Importance'),
                }}
                minWeight={OPTIMIZED_TERRITORY_AP_IMPORTANCE_MIN_UI}
                maxWeight={OPTIMIZED_TERRITORY_AP_IMPORTANCE_MAX}
              />
            </div>
          )}
        </div>
      ) : undefined,
    },
    (showRespectPreviousTerritories ? {
      value: CreateOptimizedTerritoriesAdditionalOption.RespectPreviousTerritories,
      label: <span css={radioLabelStyle}>{t('boundaryAI.Respect previous Territories')}</span>,
      children: selectedOption === CreateOptimizedTerritoriesAdditionalOption.RespectPreviousTerritories ? (
        <div css={contentContainerStyle}>
          <p css={selectLabelStyle}>{t('boundaryAI.Select Previously Saved Territory')}</p>
          <AutocompleteComponent
            onChange={(newBoundaryGroupId) => {
              if (newBoundaryGroupId) {
                onChangePreviouslySavedBoundaryGroupId(newBoundaryGroupId);
              }
            }}
            options={customBoundaryGroups.map(group => ({
              name: group.name,
              value: group.id,
            }))}
            value={previouslySavedBoundaryGroupId}
            icon={faGlobe}
            placeholder={t('Select Territory Group')}
            inPortal
            isClearable
          />
        </div>
      ) : undefined,
    } satisfies RadioGroupItem<CreateOptimizedTerritoriesAdditionalOption> : null),
    {
      value: CreateOptimizedTerritoriesAdditionalOption.NumerOfTerritories,
      label: <span css={radioLabelStyle}>{t('boundaryAI.Number of territories')}</span>,
      ariaLabel: 'Enable number of territories option',
      children: (
        <div css={numberOfTerritoriesChildrenStyle}>
          <TooltipComponent
            placement={TooltipPlacement.Bottom}
            tooltipContent={numberOfTerritoriesChangedMessage}
            behavior={TooltipBehavior.ShowAlways}
          >
            <InputWithSpinnersComponent
              isDisabled={selectedOption !== CreateOptimizedTerritoriesAdditionalOption.NumerOfTerritories}
              onChange={newValue => onChangeNumberOfTerritories(newValue)}
              onBlur={() => {
                if (selectedOption === CreateOptimizedTerritoriesAdditionalOption.NumerOfTerritories && (isNullOrUndefined(numberOfTerritories) || numberOfTerritories < WMS_MIN_NUMBER_OF_TERRITORIES)) {
                  onChangeNumberOfTerritories(WMS_MIN_NUMBER_OF_TERRITORIES);
                }
              }}
              value={numberOfTerritories ?? undefined}
              size={InputSize.Medium}
              css={{ width: 120, marginLeft: 10 }}
              valueMin={selectedOption === CreateOptimizedTerritoriesAdditionalOption.NumerOfTerritories ? WMS_MIN_NUMBER_OF_TERRITORIES : undefined}
              placeholder={selectedOption === CreateOptimizedTerritoriesAdditionalOption.NumerOfTerritories ? undefined : t('N/A')}
              aria-label="Number of territories"
            />
          </TooltipComponent>
          {numberOfTerritoriesLoading && (
            <LoaderComponent
              size={30}
              css={refreshTerritoriesIconStyle}
            />
          )}
        </div>
      ),
    },
    {
      value: CreateOptimizedTerritoriesAdditionalOption.Constraints,
      label: <span css={radioLabelStyle}>{t('Optimization with constraints')}</span>,
      style: radioItemStyle,
      children: selectedOption === CreateOptimizedTerritoriesAdditionalOption.Constraints ? (
        <AdditionalOptionsConstraintsComponent
          demographicsValues={demographicsValues}
          dataMetricsValues={dataMetricsValues}
          onChangeDataMetrics={onChangeDataMetrics}
          onChangeDemographics={onChangeDemographics}
          isLoading={constraintsValidationLoading}
          validationData={constraintsValidationData}
          generalError={constraintsGeneralError}
        />
      ) : undefined,
    },
  ].filter(notNullsy);

  return (
    <>
      <RadioGroupComponent
        selectedValue={selectedOption}
        onValueChange={onChangeSelectedOption}
        items={radioItems}
      />

      {!!locationCountValidationError && (
        <AlertComponent
          css={validationAlertStyle}
          type="danger"
        >
          {locationCountValidationError}
        </AlertComponent>
      )}

      <div css={continueButtonStyle}>
        <ContinueButton
          label="Continue"
          onSubmitClick={onContinue}
          disabled={isSubmitDisabled}
        />
      </div>
    </>
  );
};
